import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CustomSelect, CustomSelectItem, Button, ConfirmButton, PromptType } from '@hwm/ui-lib';
import { Trans, t } from '@lingui/macro';
import { Dialog, makeStyles, Theme, Typography } from '@material-ui/core';
import { CustomHeader } from '../CustomHeader';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { selectedAccStateSelector } from '@core/store/library-stores';
import { useHistory } from 'react-router-dom';
import { AppPaths } from '@core/routes/routesPaths';
import { FlowUnitsOptionsFn, PMZoneUnitsState, PreferencesModel, PressureUnitsOptionsFn } from '@shared/models';
import { preferencesSelector } from '@core/store/entity/preferences/PreferencesSelectors';
import { preferencesThunks } from '@core/store/entity/preferences/PreferencesThunks';

const useStyles = makeStyles((theme: Theme) => ({
  select: {
    height: 36,
    maxHeight: 36,
    borderWidth: 2,
    zIndex: 200,
    margin: 0,
    paddingLeft: theme.spacing(2),
    '& .select__control': {
      cursor: 'pointer',
      minWidth: 100,
    },
  },
  dialog: {
    padding: theme.spacing(3, 4, 5),
  },
}));

export const Preferences: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const [showModal, setShowModal] = useState(false);
  const [unitsState, setUnitsState] = useState<PMZoneUnitsState>(null);

  const { preferences: units, loading } = useAppSelector(preferencesSelector);
  const { userRole } = useAppSelector(selectedAccStateSelector) ?? {};

  const userRoleNoAccess = useMemo(() => userRole === null || userRole === 'No access', [userRole]);
  const userRoleAssigned = useMemo(() => !userRoleNoAccess && Boolean(userRole), [userRoleNoAccess, userRole]);

  const isControlPage = useMemo(
    () => history.location?.pathname === AppPaths.controlView.path.base,
    [history.location?.pathname]
  );

  const savePreferences = useCallback(
    (units: PreferencesModel) => {
      if (!units) return;

      dispatch(preferencesThunks.savePreferences({ body: { ...units } })).then(() => setShowModal(false));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!units || !userRoleAssigned) {
      return;
    }
    setUnitsState(units);
  }, [units, userRoleAssigned, showModal]);

  return userRoleAssigned ? (
    <>
      <Button
        className="w-full"
        variant="outlined"
        onClick={() => setShowModal(true)}
        disabled={!userRoleAssigned}
        dataTestId="layout-navbar-buttons-unit-preferences-button"
      >
        <Trans>Unit Preferences</Trans>
      </Button>
      <Dialog
        classes={{ paper: classes.dialog }}
        open={showModal}
        onClose={() => setShowModal(false)}
        data-testid="layout-navbar-buttons-unit-preferences-modal"
      >
        <CustomHeader title={t`Unit Preferences`} dataTestId="layout-navbar-buttons-unit-preferences-modal-header" />
        <div className="flex flex-row mb-8">
          <div className="mr-2">
            <Typography data-testid="layout-navbar-buttons-unit-preferences-modal-flowUnit-label">
              <Trans>Flow unit</Trans>
            </Typography>
            <CustomSelect
              label={null}
              onChange={(selectedValue: CustomSelectItem | CustomSelectItem[]) => {
                setUnitsState((p) => ({
                  pressureUnit: p.pressureUnit,
                  flowUnit: (selectedValue as CustomSelectItem).value,
                }));
              }}
              options={Object.entries(FlowUnitsOptionsFn()).map(([key, label]) => ({
                label,
                value: key,
              }))}
              minWidth={116}
              className={classes.select}
              value={
                unitsState?.flowUnit
                  ? {
                      label: FlowUnitsOptionsFn()[unitsState.flowUnit],
                      value: unitsState.flowUnit,
                    }
                  : null
              }
              isDisabled={loading}
              isError={false}
              dataTestId="layout-navbar-buttons-unit-preferences-modal-flowUnit-select"
            />
          </div>
          <div>
            <Typography data-testid="layout-navbar-buttons-unit-preferences-modal-pressureUnit-label">
              <Trans>Pressure unit</Trans>
            </Typography>
            <CustomSelect
              label={null}
              onChange={(selectedValue: CustomSelectItem | CustomSelectItem[]) => {
                setUnitsState((p) => ({
                  flowUnit: p.flowUnit,
                  pressureUnit: (selectedValue as CustomSelectItem).value,
                }));
              }}
              options={Object.entries(PressureUnitsOptionsFn()).map(([key, label]) => ({
                label,
                value: key,
              }))}
              minWidth={116}
              className={classes.select}
              value={
                unitsState?.pressureUnit
                  ? {
                      label: PressureUnitsOptionsFn()[unitsState.pressureUnit],
                      value: unitsState.pressureUnit,
                    }
                  : null
              }
              isDisabled={loading}
              isError={false}
              dataTestId="layout-navbar-buttons-unit-preferences-modal-pressureUnit-select"
            />
          </div>
        </div>
        <div className="flex flex-row justify-end">
          {Boolean(unitsState) ? (
            isControlPage ? (
              <ConfirmButton
                promptProps={{
                  content: t`There could be changes made to Control Settings. Do you want to change units? All unsaved/unsubmitted data from Control Settings will be discarded.`,
                  title: t`Unit Preferences`,
                  type: PromptType.Warning,
                  onConfirm: () => savePreferences({ ...unitsState }),
                }}
                dataTestId="layout-navbar-buttons-unit-preferences-modal-save-preferences-confirm-prompt"
              >
                {({ onClick }) => (
                  <Button
                    style={{ height: 32, width: 80, marginRight: 8 }}
                    onClick={onClick}
                    loading={loading}
                    disabled={loading}
                    dataTestId="layout-navbar-buttons-unit-preferences-modal-save-preferences-confirm-prompt-button"
                  >
                    <Trans>Save</Trans>
                  </Button>
                )}
              </ConfirmButton>
            ) : (
              <Button
                style={{ height: 32, width: 80, marginRight: 8 }}
                onClick={() => savePreferences({ ...unitsState })}
                loading={loading}
                disabled={loading}
                dataTestId="layout-navbar-buttons-unit-preferences-modal-save-preferences-button"
              >
                <Trans>Save</Trans>
              </Button>
            )
          ) : null}
          <Button
            style={{ height: 32, width: 80 }}
            onClick={() => setShowModal(false)}
            variant="outlined"
            dataTestId="layout-navbar-buttons-unit-preferences-modal-close-button"
          >
            <Trans id="[Action.Close]">Close</Trans>
          </Button>
        </div>
      </Dialog>
    </>
  ) : null;
};
